import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Shield,
  MessageSquare,
  Users,
  Settings,
  Bot,
  Crown,
  BellRing,
  Brain,
  ArrowLeft,
  Save,
  Hash,
  LineChart,
  UserPlus,
  UserMinus,
  Calendar,
  MessageCircle,
  Cog,
  Home
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const DISCORD_API_ENDPOINT = 'https://discord.com/api/v10';

const dummyServerConfig: ServerConfig = {
  id: "offline-server-id",
  name: "Offline Server",
  icon: null,
  premium: false,
  settings: {
    general: {
      prefix: "/",
      language: "en",
      timezone: "UTC",
      logChannel: "offline-logs",
    },
    moderation: {
      autoMod: false,
      antiSpam: false,
      maxWarnings: 0,
      warningTimeout: 0,
      customWords: [],
    },
    messages: {
      welcomeEnabled: false,
      welcomeMessage: "Welcome {user} to {server}! (Offline Mode)",
      welcomeChannel: "offline-welcome",
      leaveEnabled: false,
      leaveMessage: "Goodbye {user}! (Offline Mode)",
      leaveChannel: "offline-general",
      autoResponses: [],
    },
    aiFeatures: {
      chatEnabled: false,
      customPersonality: "Offline AI",
      responseStyle: "Friendly",
      channels: ["offline-ai-chat"],
    },
    notifications: {
      joinLeave: false,
      moderationActions: false,
      serverUpdates: false,
    },
  },
  stats: {
    totalMembers: 0,
    onlineMembers: 0,
    messageCount: 0,
    commandsUsed: 0,
    joinedAt: new Date().toISOString(),
    channelCount: 0,
    recentActivity: {
      joins: 0,
      leaves: 0,
      messages: 0,
      commands: 0,
    },
  },
};

function getDiscordToken() {
  return localStorage.getItem('discord_token');
}

function getStoredServerSettings(serverId: string) {
  const stored = localStorage.getItem(`server_${serverId}_settings`);
  return stored ? JSON.parse(stored) : null;
}

function saveServerSettings(serverId: string, settings: any) {
  localStorage.setItem(`server_${serverId}_settings`, JSON.stringify(settings));
}

interface ServerStats {
  totalMembers: number;
  onlineMembers: number;
  messageCount: number;
  commandsUsed: number;
  joinedAt: string;
  channelCount?: number;
  recentActivity: {
    joins: number;
    leaves: number;
    messages: number;
    commands: number;
  };
}

interface ServerConfig {
  id: string;
  name: string;
  icon?: string;
  premium: boolean;
  settings: {
    general: {
      prefix: string;
      language: string;
      timezone: string;
      logChannel: string;
    };
    moderation: {
      autoMod: boolean;
      antiSpam: boolean;
      maxWarnings: number;
      warningTimeout: number;
      customWords: string[];
    };
    messages: {
      welcomeEnabled: boolean;
      welcomeMessage: string;
      welcomeChannel: string;
      leaveEnabled: boolean;
      leaveMessage: string;
      leaveChannel: string;
      autoResponses: Array<{ trigger: string; response: string }>;
    };
    aiFeatures: {
      chatEnabled: boolean;
      customPersonality: string;
      responseStyle: "Friendly" | "Professional" | "Casual";
      channels: string[];
    };
    notifications: {
      joinLeave: boolean;
      moderationActions: boolean;
      serverUpdates: boolean;
    };
  };
  stats: ServerStats;
}

interface GuildPreview {
  id: string;
  name: string;
  icon: string | null;
  splash: string | null;
  discovery_splash: string | null;
  approximate_member_count?: number;
  approximate_presence_count?: number;
  description: string | null;
  features: string[];
  premium_tier: number;
  preferred_locale: string;
}

const ServerDetails = () => {
  const { serverId } = useParams();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [server, setServer] = useState<ServerConfig | null>(null);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");

  const localStorageKey = serverId ? `server_details_${serverId}` : null;

  const getServerIconUrl = (id: string, icon: string | null) => {
    if (!icon) return 'https://cdn.discordapp.com/embed/avatars/0.png';
    return `https://cdn.discordapp.com/icons/${id}/${icon}.png`;
  };

  useEffect(() => {
    const fetchServerDetails = async () => {
      if (!serverId) return; // Should not happen if routed correctly

      // 1. Check Cache
      if (localStorageKey) {
        const cachedData = localStorage.getItem(localStorageKey);
        if (cachedData) {
          try {
            const parsedData = JSON.parse(cachedData) as ServerConfig;
            // Ensure cached data is not from a failed state (e.g. dummy data with offline ID)
            if (parsedData.id === serverId) {
              setServer(parsedData);
              toast({
                title: "Loaded from Cache",
                description: "Displaying cached server details.",
              });
              // Optionally, you could trigger a background update here
              // For this task, we return early.
              return;
            } else {
              // Cached data is for a different server or is invalid, remove it
              localStorage.removeItem(localStorageKey);
            }
          } catch (e) {
            console.warn("Failed to parse cached server details", e);
            localStorage.removeItem(localStorageKey); // Clear corrupted cache
          }
        }
      }

      // 2. Fetch from API if not in cache or cache invalid
      try {
        const token = getDiscordToken();
        if (!token) {
          toast({
            title: "Error",
            description: "Please sign in again.",
            variant: "destructive"
          });
          return;
        }

        // Fetch guild data directly from Discord
        const guildResponse = await fetch(`${DISCORD_API_ENDPOINT}/guilds/${serverId}?with_counts=true`, {
          headers: {
            'Authorization': `Bot ${import.meta.env.VITE_DISCORD_BOT_TOKEN}`,
          }
        });

        if (!guildResponse.ok) {
          throw new Error(`Discord API error! status: ${guildResponse.status}`);
        }

        const guildData = await guildResponse.json();
        
        // Get stored settings or use defaults - This is important for when API fetches fresh data
        const storedSettings = getStoredServerSettings(serverId) || { // Ensure serverId is non-null here
          general: {
            prefix: "/",
            language: "en",
            timezone: "UTC",
            logChannel: "mod-logs",
          },
          moderation: {
            autoMod: true,
            antiSpam: true,
            maxWarnings: 3,
            warningTimeout: 24,
            customWords: ["bad", "words"],
          },
          messages: {
            welcomeEnabled: true,
            welcomeMessage: "Welcome {user} to {server}! 🎉",
            welcomeChannel: "welcome",
            leaveEnabled: true,
            leaveMessage: "Goodbye {user}! We'll miss you!",
            leaveChannel: "general",
            autoResponses: [
              { trigger: "hi", response: "Hey there!" },
              { trigger: "help", response: "Use /help to see all commands" }
            ]
          },
          aiFeatures: {
            chatEnabled: true,
            customPersonality: "Friendly and helpful bot that assists with server management",
            responseStyle: "Friendly",
            channels: ["ai-chat", "general"]
          },
          notifications: {
            joinLeave: true,
            moderationActions: true,
            serverUpdates: true,
          }
        };

        const newServerData: ServerConfig = {
          id: guildData.id,
          name: guildData.name,
          icon: guildData.icon,
          premium: guildData.premium_tier > 0,
          settings: storedSettings, // Use potentially updated settings
          stats: {
            totalMembers: guildData.approximate_member_count || 0,
            onlineMembers: guildData.approximate_presence_count || 0,
            messageCount: Math.floor(Math.random() * 10000), // Simulated
            commandsUsed: Math.floor(Math.random() * 1000), // Simulated
            channelCount: guildData.channels?.length || 0, // Make sure this is correct
            joinedAt: new Date().toISOString(), // This should ideally be from API if available
            recentActivity: {
              joins: Math.floor(Math.random() * 10), // Simulated
              leaves: Math.floor(Math.random() * 5), // Simulated
              messages: Math.floor(Math.random() * 100), // Simulated
              commands: Math.floor(Math.random() * 20) // Simulated
            }
          }
        };

        // 3. Cache the new data
        if (localStorageKey) {
          localStorage.setItem(localStorageKey, JSON.stringify(newServerData));
          // Also cache name and icon separately for the dummy fallback, though this is now less critical
          localStorage.setItem(`server_${guildData.id}_name`, guildData.name);
          if(guildData.icon) localStorage.setItem(`server_${guildData.id}_icon`, guildData.icon); else localStorage.removeItem(`server_${guildData.id}_icon`);

        }
        setServer(newServerData);

      } catch (error) {
        console.error("Fehler beim Abrufen der Server-Details:", error);
        // Use dummy data as fallback - DO NOT CACHE DUMMY DATA
        const fallbackName = localStorage.getItem(`server_${serverId}_name`) || `Server Name (Offline)`;
        setServer({
          ...dummyServerConfig,
          id: serverId || "unknown-offline-id", // serverId should be defined here
          name: fallbackName,
          icon: localStorage.getItem(`server_${serverId}_icon`) || null,
        });
        toast({
          title: "API Error - Displaying Cached/Offline Data",
          description: "Could not fetch live server details. Displaying last known or placeholder data.",
          variant: "destructive"
        });
      }
    };

    fetchServerDetails();
    
  }, [serverId, toast, localStorageKey]); // Added localStorageKey to dependencies

  const handleSave = () => {
    if (server && serverId && localStorageKey) {
      // When settings are saved, update the main cache as well
      const updatedServerData = {
        ...server,
        settings: server.settings, // server state already has the latest settings
      };
      localStorage.setItem(localStorageKey, JSON.stringify(updatedServerData));
      // Also update the specific settings storage, though it might become redundant
      saveServerSettings(serverId, server.settings); 
      toast({
        title: "Einstellungen gespeichert",
        description: "Ihre Server-Einstellungen wurden erfolgreich aktualisiert und zwischengespeichert.",
      });
    }
  };

  if (!server) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fembot-pink"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 dark:from-gray-800 dark:to-gray-900 py-24">
        <div className="container-custom">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link 
                  to="/dashboard"
                  className="text-gray-500 hover:text-fembot-pink transition-colors"
                >
                  <ArrowLeft className="w-6 h-6" />
                </Link>
                <div className="flex items-center gap-4">
                  <img 
                    src={getServerIconUrl(server.id, server.icon)}
                    alt={server.name}
                    className="w-16 h-16 rounded-full"
                  />
                  <div>
                    <h1 className="text-3xl font-bold dark:text-white">
                      {server.name}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-fembot-pink">
                        ID: {server.id}
                      </Badge>
                      {server.premium && (
                        <Badge className="bg-gradient-to-r from-amber-400 to-amber-600">
                          <Crown className="w-3 h-3 mr-1" /> Premium
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <Button
                onClick={handleSave}
                className="bg-fembot-pink hover:bg-fembot-dark-pink text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <Tabs defaultValue={activeTab} className="space-y-8">
            <TabsList className="bg-white dark:bg-gray-800 p-1 rounded-xl">
              <TabsTrigger value="overview">
                <Home className="w-4 h-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="moderation">
                <Shield className="w-4 h-4 mr-2" />
                Moderation
              </TabsTrigger>
              <TabsTrigger value="ai">
                <Brain className="w-4 h-4 mr-2" />
                AI Features
              </TabsTrigger>
              <TabsTrigger value="messages">
                <MessageCircle className="w-4 h-4 mr-2" />
                Messages
              </TabsTrigger>
              <TabsTrigger value="notifications">
                <BellRing className="w-4 h-4 mr-2" />
                Notifications
              </TabsTrigger>
              <TabsTrigger value="settings">
                <Cog className="w-4 h-4 mr-2" />
                Settings
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Total Members
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold">{server.stats.totalMembers}</div>
                      <Users className="w-4 h-4 text-gray-500" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {server.stats.onlineMembers} online
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Messages Today
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold">{server.stats.recentActivity.messages}</div>
                      <MessageSquare className="w-4 h-4 text-gray-500" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {server.stats.messageCount} total
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Commands Used
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold">{server.stats.recentActivity.commands}</div>
                      <Bot className="w-4 h-4 text-gray-500" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {server.stats.commandsUsed} total
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Member Changes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold">+{server.stats.recentActivity.joins}</div>
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-green-500" />
                        <UserMinus className="w-4 h-4 text-red-500" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {server.stats.recentActivity.leaves} left today
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Channels
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold">{server.stats.channelCount || 0}</div>
                      <Hash className="w-4 h-4 text-gray-500" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Total channels in server
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Activity</CardTitle>
                    <CardDescription>
                      Server activity over the last 24 hours
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px] flex items-center justify-center text-gray-500">
                      <LineChart className="w-6 h-6 mr-2" />
                      Activity Chart Placeholder
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                    <CardDescription>
                      Frequently used server management actions
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <Button variant="outline" className="w-full">
                        <Shield className="w-4 h-4 mr-2" />
                        Mod Panel
                      </Button>
                      <Button variant="outline" className="w-full">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Send Message
                      </Button>
                      <Button variant="outline" className="w-full">
                        <Users className="w-4 h-4 mr-2" />
                        Member List
                      </Button>
                      <Button variant="outline" className="w-full">
                        <Calendar className="w-4 h-4 mr-2" />
                        Event Panel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="moderation">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle>Auto Moderation</CardTitle>
                    <CardDescription>
                      Configure automatic moderation settings for your server
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label>Auto Moderation</Label>
                        <p className="text-sm text-gray-500">
                          Automatically moderate messages based on rules
                        </p>
                      </div>
                      <Switch
                        checked={server.settings.moderation.autoMod}
                        onCheckedChange={(checked) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              moderation: {
                                ...server.settings.moderation,
                                autoMod: checked,
                              },
                            },
                          });
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Maximum Warnings</Label>
                      <Input
                        type="number"
                        value={server.settings.moderation.maxWarnings}
                        onChange={(e) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              moderation: {
                                ...server.settings.moderation,
                                maxWarnings: parseInt(e.target.value),
                              },
                            },
                          });
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Warning Timeout (hours)</Label>
                      <Input
                        type="number"
                        value={server.settings.moderation.warningTimeout}
                        onChange={(e) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              moderation: {
                                ...server.settings.moderation,
                                warningTimeout: parseInt(e.target.value),
                              },
                            },
                          });
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Custom Word Filter</CardTitle>
                    <CardDescription>
                      Configure custom words to be filtered in your server
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label>Filtered Words (one per line)</Label>
                      <Textarea
                        value={server.settings.moderation.customWords.join("\n")}
                        onChange={(e) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              moderation: {
                                ...server.settings.moderation,
                                customWords: e.target.value.split("\n").filter(Boolean),
                              },
                            },
                          });
                        }}
                        placeholder="Enter words to filter..."
                        className="min-h-[200px]"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="ai">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle>AI Chat Settings</CardTitle>
                    <CardDescription>
                      Configure how the AI interacts in your server
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label>Enable AI Chat</Label>
                        <p className="text-sm text-gray-500">
                          Allow AI to respond to messages
                        </p>
                      </div>
                      <Switch
                        checked={server.settings.aiFeatures.chatEnabled}
                        onCheckedChange={(checked) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              aiFeatures: {
                                ...server.settings.aiFeatures,
                                chatEnabled: checked,
                              },
                            },
                          });
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>AI Personality</Label>
                      <Textarea
                        value={server.settings.aiFeatures.customPersonality}
                        onChange={(e) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              aiFeatures: {
                                ...server.settings.aiFeatures,
                                customPersonality: e.target.value,
                              },
                            },
                          });
                        }}
                        placeholder="Describe the AI's personality..."
                        className="min-h-[100px]"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="messages">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle>Welcome Messages</CardTitle>
                    <CardDescription>
                      Configure welcome messages for new members
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label>Enable Welcome Messages</Label>
                        <p className="text-sm text-gray-500">
                          Send a message when new members join
                        </p>
                      </div>
                      <Switch
                        checked={server.settings.messages.welcomeEnabled}
                        onCheckedChange={(checked) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              messages: {
                                ...server.settings.messages,
                                welcomeEnabled: checked,
                              },
                            },
                          });
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Welcome Channel</Label>
                      <Input
                        value={server.settings.messages.welcomeChannel}
                        onChange={(e) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              messages: {
                                ...server.settings.messages,
                                welcomeChannel: e.target.value,
                              },
                            },
                          });
                        }}
                        placeholder="Channel name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Welcome Message</Label>
                      <Textarea
                        value={server.settings.messages.welcomeMessage}
                        onChange={(e) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              messages: {
                                ...server.settings.messages,
                                welcomeMessage: e.target.value,
                              },
                            },
                          });
                        }}
                        placeholder="Welcome message template"
                      />
                      <p className="text-xs text-gray-500">
                        Variables: {"{user}"}, {"{server}"}, {"{memberCount}"}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Auto Responses</CardTitle>
                    <CardDescription>
                      Set up automatic responses to specific messages
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {server.settings.messages.autoResponses.map((response, index) => (
                      <div key={index} className="space-y-2 p-4 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <Label>Trigger</Label>
                          <Input
                            value={response.trigger}
                            onChange={(e) => {
                              const newResponses = [...server.settings.messages.autoResponses];
                              newResponses[index].trigger = e.target.value;
                              setServer({
                                ...server,
                                settings: {
                                  ...server.settings,
                                  messages: {
                                    ...server.settings.messages,
                                    autoResponses: newResponses,
                                  },
                                },
                              });
                            }}
                            className="w-2/3"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label>Response</Label>
                          <Input
                            value={response.response}
                            onChange={(e) => {
                              const newResponses = [...server.settings.messages.autoResponses];
                              newResponses[index].response = e.target.value;
                              setServer({
                                ...server,
                                settings: {
                                  ...server.settings,
                                  messages: {
                                    ...server.settings.messages,
                                    autoResponses: newResponses,
                                  },
                                },
                              });
                            }}
                            className="w-2/3"
                          />
                        </div>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      onClick={() => {
                        setServer({
                          ...server,
                          settings: {
                            ...server.settings,
                            messages: {
                              ...server.settings.messages,
                              autoResponses: [
                                ...server.settings.messages.autoResponses,
                                { trigger: "", response: "" },
                              ],
                            },
                          },
                        });
                      }}
                      className="w-full"
                    >
                      Add Auto Response
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="notifications">
              <Card>
                <CardHeader>
                  <CardTitle>Notification Settings</CardTitle>
                  <CardDescription>
                    Configure which events trigger notifications in your server
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Member Join/Leave</Label>
                      <p className="text-sm text-gray-500">
                        Notify when members join or leave
                      </p>
                    </div>
                    <Switch
                      checked={server.settings.notifications.joinLeave}
                      onCheckedChange={(checked) => {
                        setServer({
                          ...server,
                          settings: {
                            ...server.settings,
                            notifications: {
                              ...server.settings.notifications,
                              joinLeave: checked,
                            },
                          },
                        });
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Moderation Actions</Label>
                      <p className="text-sm text-gray-500">
                        Notify about warnings, kicks, and bans
                      </p>
                    </div>
                    <Switch
                      checked={server.settings.notifications.moderationActions}
                      onCheckedChange={(checked) => {
                        setServer({
                          ...server,
                          settings: {
                            ...server.settings,
                            notifications: {
                              ...server.settings.notifications,
                              moderationActions: checked,
                            },
                          },
                        });
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Server Updates</Label>
                      <p className="text-sm text-gray-500">
                        Notify about server setting changes
                      </p>
                    </div>
                    <Switch
                      checked={server.settings.notifications.serverUpdates}
                      onCheckedChange={(checked) => {
                        setServer({
                          ...server,
                          settings: {
                            ...server.settings,
                            notifications: {
                              ...server.settings.notifications,
                              serverUpdates: checked,
                            },
                          },
                        });
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings">
              <Card>
                <CardHeader>
                  <CardTitle>General Settings</CardTitle>
                  <CardDescription>
                    Configure basic server settings and preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Bot Prefix</Label>
                      <Input
                        value={server.settings.general.prefix}
                        onChange={(e) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              general: {
                                ...server.settings.general,
                                prefix: e.target.value,
                              },
                            },
                          });
                        }}
                        placeholder="!"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Language</Label>
                      <Select
                        value={server.settings.general.language}
                        onValueChange={(value) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              general: {
                                ...server.settings.general,
                                language: value,
                              },
                            },
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="de">Deutsch</SelectItem>
                          <SelectItem value="fr">Français</SelectItem>
                          <SelectItem value="es">Español</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Timezone</Label>
                      <Select
                        value={server.settings.general.timezone}
                        onValueChange={(value) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              general: {
                                ...server.settings.general,
                                timezone: value,
                              },
                            },
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UTC">UTC</SelectItem>
                          <SelectItem value="Europe/London">London</SelectItem>
                          <SelectItem value="Europe/Berlin">Berlin</SelectItem>
                          <SelectItem value="America/New_York">New York</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Log Channel</Label>
                      <Input
                        value={server.settings.general.logChannel}
                        onChange={(e) => {
                          setServer({
                            ...server,
                            settings: {
                              ...server.settings,
                              general: {
                                ...server.settings.general,
                                logChannel: e.target.value,
                              },
                            },
                          });
                        }}
                        placeholder="mod-logs"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default ServerDetails;